import React, { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link } from '@mui/material';
import { ChevronLeft as BackIcon, Telegram as TelegramIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/authApi';
import { BOT_URL, BOT_USERNAME } from '../config/telegram';
import { tokens } from '../theme/tokens';
import FinMark from '../components/brand/FinMark';
import LinkStep from '../components/onboarding/steps/LinkStep';
import { CompleteAccountDialog } from '../components/auth/CompleteAccountDialog';

const COMMANDS = ['summary', 'balance', 'contexts', 'help'] as const;

const card = {
  bgcolor: tokens.surface,
  border: `1px solid ${tokens.line}`,
  borderRadius: `${tokens.radius.card}px`,
  p: { xs: 2.25, md: 3.5 },
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
} as const;

/**
 * Telegram in Ajustes: linking with the same one-tap code as the
 * onboarding; once linked, who it is, the way back to Fin, the shortcuts,
 * and unlinking.
 */
const TelegramSettingsPage: React.FC = () => {
  const { t } = useTranslation('telegram');
  const { t: tOnboarding } = useTranslation('onboarding');
  const { t: tAuth } = useTranslation('auth');
  const { state, applyUser } = useAuth();
  const user = state.user;
  const linked = Boolean(user?.isTelegramLinked);
  const titleId = useId();
  const [confirming, setConfirming] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);

  const unlink = async () => {
    setUnlinking(true);
    setError(null);
    try {
      applyUser(await authApi.unlinkTelegram());
      setConfirming(false);
    } catch (failure: any) {
      setError(failure.response?.data?.message || t('messages.errorDisconnecting'));
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2.5 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Link component={RouterLink} to='/settings' underline='none' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, alignSelf: 'flex-start', fontSize: 14, fontWeight: 600, minHeight: 32 }}>
          <BackIcon fontSize='small' />
          {t('page.back')}
        </Link>
        <Box component='h1' sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 26, md: 30 }, fontWeight: 700, letterSpacing: '-0.025em' }}>
          {t('title')}
        </Box>
      </Box>

      {error && <Alert severity='error'>{error}</Alert>}

      {linked ? (
        <>
          <Box component='section' aria-label={t('page.connectedTitle')} sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FinMark size={52} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: 22, fontWeight: 700 }}>
                  {user?.telegramUsername ? t('page.connectedAs', { username: user.telegramUsername }) : t('page.connectedTitle')}
                </Box>
                <Box component='span' sx={{ fontSize: 14.5, color: tokens.ink2 }}>{t('page.connectedText')}</Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
              <Button variant='contained' startIcon={<TelegramIcon />} href={BOT_URL} target='_blank' rel='noopener noreferrer' sx={{ minHeight: 44 }}>
                {t('page.open')}
              </Button>
              {/* A Telegram profile signs in through its Telegram account: it has no other way in. */}
              {!user?.isGuest && (
                <Button variant='text' onClick={() => setConfirming(true)} sx={{ minHeight: 44, color: tokens.danger }}>
                  {t('actions.disconnect')}
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: { xs: 2, md: 2.5 } }}>
            <Box component='section' aria-label={tOnboarding('talk.commandsLabel')} sx={card}>
              <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: 18, fontWeight: 650 }}>{tOnboarding('talk.commandsLabel')}</Box>
              <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {COMMANDS.map((command) => (
                  <Box component='li' key={command} sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', fontSize: 14 }}>
                    <Box component='code' sx={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 13, px: 1, py: 0.25, borderRadius: '8px', bgcolor: tokens.bg, whiteSpace: 'nowrap' }}>
                      /{tOnboarding(`talk.commands.${command}.name`)}
                    </Box>
                    <span>{tOnboarding(`talk.commands.${command}.text`)}</span>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component='section' aria-label={t('page.groupsTitle')} sx={card}>
              <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: 18, fontWeight: 650 }}>{t('page.groupsTitle')}</Box>
              <Box component='p' sx={{ m: 0, fontSize: 14.5, color: tokens.ink2, lineHeight: 1.55 }}>
                {t('page.groupsText', { bot: `@${BOT_USERNAME}` })}
              </Box>
            </Box>
          </Box>
        </>
      ) : user?.isGuest ? (
        // A Telegram profile gets no linking code (the bot's question could
        // only name it by a name it chose): it completes its account first,
        // and then this page offers the code.
        <Box component='section' aria-labelledby={titleId} sx={card}>
          <Box component='h2' id={titleId} sx={{ m: 0, fontFamily: tokens.display, fontSize: 22, fontWeight: 700 }}>
            {t('page.guestTitle')}
          </Box>
          <Box component='p' sx={{ m: 0, fontSize: 14.5, color: tokens.ink2, lineHeight: 1.55 }}>
            {t('page.guestText')}
          </Box>
          <Button variant='contained' onClick={() => setCompleting(true)} sx={{ alignSelf: 'flex-start', minHeight: 44 }}>
            {tAuth('guest.bannerAction')}
          </Button>
          <CompleteAccountDialog open={completing} onClose={() => setCompleting(false)} />
        </Box>
      ) : (
        <Box component='section' aria-labelledby={titleId} sx={card}>
          <LinkStep titleId={titleId} hideNote />
        </Box>
      )}

      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth='xs' fullWidth>
        <DialogTitle>{t('dialogs.disconnect.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('dialogs.disconnect.message')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>{t('dialogs.disconnect.cancelButton')}</Button>
          <Button variant='contained' color='error' onClick={unlink} disabled={unlinking}>
            {t('dialogs.disconnect.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TelegramSettingsPage;
